// src/actions/social.js
// Thunks + sync actions for the nested user.brands structure

import {
  SOCIAL_SET_LOADING,
  SOCIAL_SET_TOKENS,
  SOCIAL_SET_ERROR,
  SOCIAL_CONNECT_REQUEST,
  SOCIAL_CONNECT_URL_READY,
  SOCIAL_CONNECT_PAYLOAD,
  SOCIAL_CONNECT_SUCCESS,
  SOCIAL_CONNECT_FAILURE,
  SOCIAL_DISCONNECT_SUCCESS,
  SOCIAL_ACCOUNTS_REQUEST,
  SOCIAL_ACCOUNTS_SUCCESS,
  SOCIAL_ACCOUNTS_FAILURE,
  SOCIAL_TARGETS_LOADED,
  SOCIAL_TARGET_SELECTED,
  SOCIAL_MESSAGE,
  RESET_BRAND_ACTIVE_CONTEXT,
  SET_DEFAULT_SOCIAL_TARGET,
  APPLY_CONNECT_PAYLOAD,
  SET_BRANDS,
  UPSERT_SOCIAL_ACCOUNT,
  REMOVE_SOCIAL_ACCOUNT,
  UPSERT_SOCIAL_TARGET,
} from '../constants/action-types/social';


import {
  connectSocialMedia,
  disconnectSocialMedia,
  getAllSocialAccounts,
  getSocialAccount,
  getSocialAccountTargets,
  saveSocialTarget,
  setDefaultSocialTarget as setDefaultSocialTargetAPI,
} from '../api/socialMedia/socialMedia';


import {
  startBrandConnectSession,
  commitBrandConnectSession,
  abandonBrandConnectSession,
} from '../api/brands/brand';

import { SOCIAL_SESSION_HYDRATE, SOCIAL_SESSION_CLEAR } from '../reducers/social';

// ---- Local action-type strings expected by your user reducer ----


export const connectRequest = (platform, brandSettingsId) => ({
  type: SOCIAL_CONNECT_REQUEST,
  platform,
  brandSettingsId
});

// ----------------------- Sync actions -----------------------
export const setSocialLoading = (bool) => ({ type: SOCIAL_SET_LOADING, payload: !!bool });
export const setSocialError   = (err)  => ({ type: SOCIAL_SET_ERROR,  payload: err });
export const setSocialTokens  = (platform, payload) => ({ type: SOCIAL_SET_TOKENS, platform, payload });
export const setSocialMessage = (message) => ({ type: SOCIAL_MESSAGE, payload: message });

 export const resetBrandActiveContext = () => ({
   type: RESET_BRAND_ACTIVE_CONTEXT,
    payload: {brandId: null , brandSettingsId: null}
 });




// ----------------------- Helpers -----------------------
const idOf = (x) => (x && (x._id || x.id)) || x;

function normalizeTarget(platform, accountId, t) {
  const p = String(platform || '').toLowerCase();
  const externalId = String(
    t?.externalId ?? t?.id ?? t?.pageId ?? t?.channelId ?? t?.username ?? t?.urn ?? ''
  );
  const composedId = `${p}:${externalId}`;
  return {
    _id: composedId,
    platform: p,
    externalId,
    name: t?.name || t?.pageName || t?.title || t?.localizedName || t?.username || 'Destination',
    type:
      t?.type ||
      (p === 'linkedin'
        ? (externalId.includes('organization') ? 'li_organization' : 'li_person')
        : p === 'facebook'
        ? 'facebook_page'
        : p === 'instagram'
        ? 'instagram_business'
        : `${p}_target`),
    avatarUrl: t?.avatarUrl || t?.pictureUrl || t?.icon || null,
    tokenExpiresAt: t?.tokenExpiresAt || null,
    account: String(accountId),
    isDefault: false,
  };
}

function normalizeTargetsArray(platform, accountId, rawTargets = []) {
  return (rawTargets || []).map((t) => normalizeTarget(platform, accountId, t));
}

// Build a brands[] payload for SET_BRANDS from a flat accounts list
function accountsToBrandsTree(accounts = []) {
  const byBrand = new Map(); // brandId -> brand node
  for (const acc of accounts) {
    const brandId = String(acc.brand_id || acc.brandId || 'unknown');
    const brand = byBrand.get(brandId) || {
      _id: brandId,
      name: acc.brandName,           // optional
      users: [],
      posts: [],
      ideas: [],
      brandSettings: { socialAccounts: [], socialTargets: [] },
    };

    // ensure account has _id and platform
    const accountRecord = {
      ...(acc || {}),
      _id: String(idOf(acc)),
      platform: String(acc.platform || '').toLowerCase(),
      status:
        acc?.status
        ?? ((acc?.accessToken || acc?.refreshToken || (Array.isArray(acc?.pages) && acc.pages.length > 0))
             ? 'connected'
             : 'disconnected'),
    };
    brand.brandSettings.socialAccounts = upsertById(
      brand.brandSettings.socialAccounts,
      accountRecord,
      '_id'
    );

    // merge defaultTarget/targets into socialTargets
    if (acc?.defaultTarget?.id) {
      const dt = normalizeTarget(accountRecord.platform, accountRecord._id, {
        id: acc.defaultTarget.id,
        name: acc.defaultTarget.name,
        type: acc.defaultTarget.type,
        avatarUrl: acc.defaultTarget.avatarUrl,
      });
      dt.isDefault = true;
      brand.brandSettings.socialTargets = upsertById(
        brand.brandSettings.socialTargets,
        dt,
        '_id'
      );
    }
    if (Array.isArray(acc?.targets)) {
      const ts = normalizeTargetsArray(accountRecord.platform, accountRecord._id, acc.targets);
      for (const t of ts) {
        brand.brandSettings.socialTargets = upsertById(
          brand.brandSettings.socialTargets,
          t,
          '_id'
        );
      }
    }

    byBrand.set(brandId, brand);
  }
  return Array.from(byBrand.values());
}

function upsertById(arr = [], item, key = '_id') {
  if (!item) return arr.slice();
  const val = item[key];
  const idx = arr.findIndex((x) => String(x[key]) === String(val));
  if (idx === -1) return [...arr, item];
  const next = arr.slice();
  next[idx] = { ...arr[idx], ...item };
  return next;
}

// ----------------------- Thunks -----------------------


// Helper: find Brand & BrandSettings from state
const findBrandAndSettings = (state, brandId, brandSettingsId) => {
  const brands = state?.user?.brands || [];

  let brand = null;
  if (brandId) {
    brand = brands.find(b => String(b?._id) === String(brandId)) || null;
  }
  if (!brand && brandSettingsId) {
    brand = brands.find(b => String(b?.brandSettings?._id) === String(brandSettingsId)) || null;
  }
  const brandSettings = brand?.brandSettings || null;
  return { brand, brandSettings };
};

// Build a defensive byPlatform if server didn’t denormalize it
const synthesizeByPlatform = (brandSettings = {}) => {
  const byPlatform = {};
  (brandSettings.socialAccounts || []).forEach(a => {
    const p = String(a.platform || '').toLowerCase();
    if (!p) return;
    if (!byPlatform[p]) byPlatform[p] = { accounts: [], targets: [], adsAccounts: [], selected: {} };
    byPlatform[p].accounts.push(a);
  });
  (brandSettings.socialTargets || []).forEach(t => {
    const p = String(t.platform || '').toLowerCase();
    if (!p) return;
    if (!byPlatform[p]) byPlatform[p] = { accounts: [], targets: [], adsAccounts: [], selected: {} };
    byPlatform[p].targets.push(t);
  });
  (brandSettings.adsAccounts || []).forEach(ad => {
    const p = String(ad.platform || '').toLowerCase();
    if (!p) return;
    if (!byPlatform[p]) byPlatform[p] = { accounts: [], targets: [], adsAccounts: [], selected: {} };
    byPlatform[p].adsAccounts.push(ad);
  });
  return byPlatform;
};

// Public thunk: hydrate or clear session connections when the modal opens
export const hydrateSocialFromBrandSettings = (brandId, brandSettingsId) => (dispatch, getState) => {
  if (!brandId || !brandSettingsId) {
    // Create mode or incomplete context → clear ephemeral connections
    dispatch({ type: SOCIAL_SESSION_CLEAR });
    return;
  }

  const state = getState();
  const { brandSettings } = findBrandAndSettings(state, brandId, brandSettingsId);

  if (!brandSettings) {
    // No data to hydrate → clear to avoid stale overlays
    dispatch({ type: SOCIAL_SESSION_CLEAR });
    return;
  }

  const byPlatform = brandSettings.byPlatform && Object.keys(brandSettings.byPlatform).length
    ? brandSettings.byPlatform
    : synthesizeByPlatform(brandSettings);

  dispatch({
    type: SOCIAL_SESSION_HYDRATE,
    payload: {
      brandId,
      brandSettingsId,
      byPlatform,
      selectedByPlatform: brandSettings.selectedByPlatform || {}
    }
  });
};

// Convenience export to clear explicitly (optional)
export const clearSocialSession = () => ({ type: SOCIAL_SESSION_CLEAR });


 // accept optional ctx so the caller can pass brandId / brandSettingsId
 export const startConnect = (platform, ctx = {}) => async (dispatch) => {
  const p = String(platform || '').toLowerCase();
  try {
    dispatch({ type: SOCIAL_CONNECT_REQUEST, platform: p });

    // If caller provided IDs, use them; otherwise create a connect-session
    let brandId = ctx.brandId || null;
    let brandSettingsId = ctx.brandSettingsId || null;
    if (!brandId || !brandSettingsId) {
      const s = await startBrandConnectSession();   // your existing API
      brandId = brandId || s.brandId || s.brand?._id || null;
      brandSettingsId = brandSettingsId || s.brandSettingsId || s.brandSettings?._id || null;
    }

// 2) get provider auth URL *with* ctx (brandId, brandSettingsId). switchAccount
//    forces the provider's account picker (Instagram/Facebook re-auth) — only
//    set by a "use a different account" action, so routine reconnects stay
//    silent (no password prompt every time).
   const data = await connectSocialMedia(p, { brandId, brandSettingsId, switchAccount: ctx.switchAccount ? 1 : undefined });

    // (optional) keep a tiny breadcrumb in sessionStorage in case of SPA reload
    try {
    sessionStorage.setItem('brandConnectCtx', JSON.stringify({ brandId, brandSettingsId, platform: p }));
    } catch {}

    dispatch({
      type: SOCIAL_CONNECT_URL_READY,
      platform: p,
      payload: { ...data, brandId, brandSettingsId },
    });
    return { ...data, brandId, brandSettingsId };
  } catch (e) {
    dispatch({
      type: SOCIAL_CONNECT_FAILURE,
      platform: p,
      error: e?.message || 'Failed to get auth URL',
    });
    throw e;
  }
};

export const connectPayload = (payload) => ({
  type: SOCIAL_CONNECT_PAYLOAD,
  platform: payload.platform,
  brandSettingsId: payload.brandSettingsId || null,
  payload
});

export const selectTarget = ({ platform, brandSettingsId, target, adsAccountId }) => ({
  type: SOCIAL_TARGET_SELECTED,
  platform,
  brandSettingsId,
  payload: { target, adsAccountId }
});

// new
export const clearDraft = (brandSettingsId) => ({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId });
export const rekeyDraft = (from, to)        => ({ type: 'SOCIAL_DRAFT_REKEY', from, to });
export const clearSocialDraft = (brandSettingsId) => ({
  type: 'SOCIAL_DRAFT_CLEAR',
  brandSettingsId
});

export const clearAllSocialDrafts = () => ({
  type: 'SOCIAL_DRAFT_CLEAR_ALL'
});
// optional commit thunk
export const commitDraftToUserBrands = (brandId, brandSettingsId) => (dispatch, getState) => {
  dispatch({ type: 'SOCIAL_DRAFT_COMMIT_REQUEST' });
  const draft = getState().social.drafts[String(brandSettingsId)] || null;
  if (!draft) {
    dispatch({ type: 'SOCIAL_DRAFT_COMMIT_FAILURE', error: 'Nothing to commit' });
    return;
  }

  // TODO: transform & dispatch into your userbrands reducer,
  // e.g. dispatch({ type: 'USERBRANDS_MERGE_SOCIAL', payload: { brandId, brandSettingsId, draft } });

  dispatch({ type: 'SOCIAL_DRAFT_COMMIT_SUCCESS' });
  dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId });
};

/** Apply OAuth payload into nested user.brands[...] */
/** Apply OAuth payload into nested user.brands[...] */
export const applyConnectPayload = (payload) => (dispatch, getState) => {
  if (!payload || typeof payload !== 'object') return;

  // Normalize core fields so we can safely use them regardless of payload shape.
  const platform =
    String(
      (payload.account && payload.account.platform) ||
      payload.platform ||
      'unknown'
    ).toLowerCase();

  const accountId =
    String(
      (payload.account && (payload.account._id || payload.account.id)) ||
      payload.accountId ||
      ''
    );

  const brandId =
    String(payload.brandId || payload.brand_id || '');

  const brandSettingsId =
    String(payload.brandSettingsId || payload.brandSettings_id || '');

  // Build a minimal account object if one wasn't provided by the popup
  const account = payload.account || {
    _id: accountId,
    platform,
    accountName: payload.user && payload.user.name ? payload.user.name : null,
    status: payload.status || 'connected',
    avatar: null,
    socialHandle: null,
    socialHeading: null,
    followers: null,
    expiresAt: null,
    defaultTarget: null,
  };

  const targets = Array.isArray(payload.targets) ? payload.targets : [];
  const adsAccounts = Array.isArray(payload.adsAccounts) ? payload.adsAccounts : [];
  // Restored targets[0] fallback so the dropdown / card show a destination
  // from the moment OAuth completes (user preference). Whatever ends up on
  // the backend is driven by the dropdown's onChange auto-save in SMC, so
  // the last value the user picks wins regardless of this initial display.
  const defaultTargetExternalId =
    payload.defaultTargetExternalId ||
    (account.defaultTarget && account.defaultTarget.id) ||
    (targets[0] && (targets[0].id || targets[0].externalId)) ||
    null;

  if (!brandId) {
    console.error('applyConnectPayload(): missing brandId in payload');
    return;
  }

  // 1) Update the "social" slice so the connect dialog has data to render
  dispatch({
    type: SOCIAL_CONNECT_PAYLOAD,
    payload: {
      platform,
      accountId,
      user: account.accountName ? { name: account.accountName } : (payload.user || null),
      targets,
      adsAccounts
    }
  });

  // 2) Update nested user.brands with the new/updated account & targets
  dispatch(updateBrandSocialAccount(brandId, { ...account, _id: accountId, platform }, targets));

  // 3) If we know the brandSettings + a default destination, persist the selection
  if (brandSettingsId && defaultTargetExternalId) {
    dispatch(updateBrandSettingsSelection(
      brandId,
      brandSettingsId,
      platform,
      accountId,
      defaultTargetExternalId
    ));
  }

  // 4) Mark connection as successful
  dispatch({
    type: SOCIAL_CONNECT_SUCCESS,
    platform,
    accountId
  });
};


/**
 * Update brand's social accounts in user reducer
 */
const updateBrandSocialAccount = (brandId, account, targets) => ({
  type: 'UPDATE_BRAND_SOCIAL_ACCOUNT',
  payload: {
    brandId,
    account,
    targets
  }
});

/**
 * Update brandSettings selectedByPlatform
 */
const updateBrandSettingsSelection = (brandId, brandSettingsId, platform, accountId, targetId) => ({
  type: 'UPDATE_BRAND_SETTINGS_SELECTION',
  payload: {
    brandId,
    brandSettingsId,
    platform,
    accountId,
    targetId
  }
});






/** Disconnect a social platform for a specific brand. Both ids accepted —
 *  server prefers brandSettingsId since SocialAccounts are keyed there, but
 *  brandId is enough when the brand is well-formed (server resolves the
 *  matching settings doc). Without either, the server 400s. */
export const doDisconnect = (platform, ctx = {}) => async (dispatch) => {
  const p = String(platform || '').toLowerCase();
  const body = {};
  if (ctx.brandId) body.brandId = String(ctx.brandId);
  if (ctx.brandSettingsId) body.brandSettingsId = String(ctx.brandSettingsId);
  try {
    const res = await disconnectSocialMedia(p, body);
    dispatch({ type: SOCIAL_DISCONNECT_SUCCESS, platform: p, payload: res });
    // Also update the brand-scoped user.brands slice that SMA reads
    // from — REMOVE_BRAND_SOCIAL_ACCOUNT strips the disconnected
    // platform's accounts/targets from the brand so the row flips
    // to 'disconnected' on the next render. Without this, the
    // SOCIAL_DISCONNECT_SUCCESS above only updates state.social
    // (which the row no longer overlays from after the cross-brand
    // leak fix) and the row stays red until a manual page refresh.
    if (ctx.brandId) {
      dispatch({
        type: 'REMOVE_BRAND_SOCIAL_ACCOUNT',
        payload: {
          brandId: String(ctx.brandId),
          brandSettingsId: ctx.brandSettingsId ? String(ctx.brandSettingsId) : null,
          platform: p,
        },
      });
    }
    dispatch(setSocialMessage(`${p} disconnected`));
    return res;
  } catch (e) {
    dispatch(setSocialError(e?.message || 'Failed to disconnect'));
    throw e;
  }
};

/**
 * Load all accounts and publish them into user.brands[] so
 * SocialMediaAccounts.jsx (which reads from user.brands) updates.
 */
export const loadAllAccounts = () => async (dispatch, getState) => {
  try {
    dispatch({ type: SOCIAL_ACCOUNTS_REQUEST });
    const list = await getAllSocialAccounts();
    const state = getState();
    const firstBrand = (state?.user?.brands || [])[0];
    const cached = (() => {
      try { return JSON.parse(sessionStorage.getItem('brandConnectCtx') || '{}'); }
      catch { return {}; }
    })();
    const fallbackBrandId = String(cached?.brandId || firstBrand?._id || '');
    const listWithBrand = list.map(a =>
      a?.brand_id ? a : { ...a, brand_id: fallbackBrandId }
    );
 dispatch({ type: SOCIAL_ACCOUNTS_SUCCESS, payload: listWithBrand });
    const brands = accountsToBrandsTree(listWithBrand);


    dispatch({ type: SET_BRANDS, payload: brands });
  } catch (e) {
    dispatch({ type: SOCIAL_ACCOUNTS_FAILURE, error: e?.message || 'Failed to load accounts' });
  }
};

/** Load one account + targets (deep link) and write nested */
export const loadAccountWithTargets = (accountId) => async (dispatch) => {
  try {
    dispatch(setSocialLoading(true));

    const [acc, tdata] = await Promise.all([
      getSocialAccount(accountId),
      getSocialAccountTargets(accountId).catch(() => ({ targets: [] })),
    ]);

    const platform = String(acc?.platform || 'unknown').toLowerCase();
    const brandId = String(acc?.brand_id || acc?.brandId || '');
    const accId = String(acc?._id || accountId);

    // upsert account
    dispatch({
      type: UPSERT_SOCIAL_ACCOUNT,
      payload: { brandId, record: { ...(acc || {}), _id: accId, platform, status: acc?.status || 'connected' } },
    });

    // upsert targets
    const targets = normalizeTargetsArray(platform, accId, tdata?.targets || acc?.targets || acc?.pages || []);
    targets.forEach((t) => {
      dispatch({ type: UPSERT_SOCIAL_TARGET, payload: { brandId, record: t } });
    });

    // optional: default target
    if (acc?.defaultTarget?.id) {
      dispatch({
        type: SET_DEFAULT_SOCIAL_TARGET,
        payload: { brandId, accountId: accId, targetId: `${platform}:${acc.defaultTarget.id}` },
      });
    }

    dispatch(setSocialLoading(false));
  } catch (e) {
    dispatch(setSocialLoading(false));
    dispatch(setSocialError(e?.message || 'Failed to load account'));
  }
};

/** Persist a chosen destination; then reflect it in nested state */
export const persistTargetSelection = (accountId, body) => async (dispatch, getState) => {
  await saveSocialTarget(accountId, body);

  const p = String(body?.platform || 'unknown').toLowerCase();
  const accId = String(accountId);
  // find brandId from state: look through user.brands
  const state = getState();
  const brands = state?.user?.brands || [];
  const brand = brands.find((b) =>
    (b?.brandSettings?.socialAccounts || []).some((a) => String(a?._id) === accId)
  );
  const brandId = brand ? String(brand._id) : '';

  // upsert the target
  const record = normalizeTarget(p, accId, {
    id: body?.targetId,
    name: body?.name,
    type: body?.targetType,
  });
  dispatch({ type: UPSERT_SOCIAL_TARGET, payload: { brandId, record } });

  // set default to that target
  dispatch({
    type: SET_DEFAULT_SOCIAL_TARGET,
    payload: { brandId, accountId: accId, targetId: `${p}:${String(body?.targetId)}` },
  });

  dispatch(setSocialMessage('Connection saved successfully!'));
};

/** Persist and flip default on client */
export const setDefaultSocialTarget = (accountId, platform, targetId) => (dispatch, getState) => {
  const state = getState();
  
  // Find the brand that owns this account
  const brands = state.user?.brands || [];
  const brand = brands.find(b => 
    b.brandSettings?.socialAccounts?.some(a => a._id === accountId)
  );
  
  if (!brand) {
    console.error('Brand not found for account:', accountId);
    return;
  }

  dispatch({
    type: 'SET_BRAND_DEFAULT_TARGET',
    payload: {
      brandId: brand._id,
      platform,
      accountId,
      targetId
    }
  });
};


/**
 * Finalize the draft brand: flip deleted:false and patch metadata.
 * `details` example:
 * {
 *   brandId,
 *   name, description, img,
 *   settingsPatch: { language, postingTime, brandThemePrimaryColor, ... }
 * }
 */
export const commitBrandWizard = (details) => async (dispatch) => {
  const saved = await commitBrandConnectSession(details);
  // easiest way to mirror backend is to reload accounts -> rebuild brands tree
  await dispatch(loadAllAccounts());
  // (optional) toast
  dispatch(setSocialMessage('Brand created and connected.'));
  return saved;
};

/** Cancel the wizard: keep the draft soft-deleted (server-side). */
export const cancelBrandWizard = (brandId) => async (dispatch) => {
  await abandonBrandConnectSession(brandId);
  // remove any optimistic local state by reloading
  await dispatch(loadAllAccounts());
  dispatch(setSocialMessage('Canceled. Draft removed.'));
};
